/**
 * SkinAdvisorBox — "Lumi" Karine's AI Skincare Advisor
 * Iter 402.2 · Aug 2026 — colour + animated mascot, bilingual, high-signal.
 *
 * Sits directly under the shop hero. Client types their skin concern in
 * plain language, we call Claude Sonnet 4.6 with the LIVE product
 * catalog as grounding, and we render an expert MIXED-BRAND routine with
 * 2-3 sentence reasoning per product. Rx items flag "Reserve at clinic".
 */
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { LumiBookingProposal } from "@/components/shop/LumiBookingProposal";
import axios from "axios";
import { Loader2, ArrowRight, ShieldAlert, RefreshCcw, Sparkles, Mail, Check, Volume2, VolumeX } from "lucide-react";
import { PhotoButton, MicButton } from "./LumiInputTools";
import { PhotoFindings, RoutinePlan } from "./LumiResultPanels";

const LUMI_EXTRA = {
  en: {
    photo: "Snap a selfie", photoReady: "Photo ready — Lumi will read it", mic: "Talk to Lumi", micStop: "Stop & send", micBusy: "Listening…",
    micError: "Lumi couldn't hear that — try again.", micDenied: "Microphone access was blocked.",
    toolsHint: "Any language works — Lumi replies in yours. Photos are analysed instantly and never stored.",
    photoHeading: "What Lumi sees in your photo", overall: "Overall",
    scoreLabels: { pigmentation: "Pigmentation", redness: "Redness", texture: "Texture", fine_lines: "Fine lines", hydration: "Hydration", pores: "Pores" },
    planHeading: "Your 4-week routine", am: "Morning", pm: "Evening", schedule: "Actives schedule", do: "Do", expect: "Expect", avoid: "Never combine",
  },
  fr: {
    photo: "Prendre un selfie", photoReady: "Photo prête — Lumi va l'analyser", mic: "Parler à Lumi", micStop: "Arrêter et envoyer", micBusy: "J'écoute…",
    micError: "Je n'ai pas bien entendu — réessayez.", micDenied: "L'accès au micro a été refusé.",
    toolsHint: "Toutes les langues fonctionnent — Lumi répond dans la vôtre. Les photos sont analysées à l'instant et jamais conservées.",
    photoHeading: "Ce que Lumi voit sur votre photo", overall: "Global",
    scoreLabels: { pigmentation: "Pigmentation", redness: "Rougeurs", texture: "Texture", fine_lines: "Ridules", hydration: "Hydratation", pores: "Pores" },
    planHeading: "Votre routine de 4 semaines", am: "Matin", pm: "Soir", schedule: "Calendrier des actifs", do: "Faire", expect: "À quoi s'attendre", avoid: "À ne jamais combiner",
  },
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  en: {
    eyebrow: "Meet Lumi · Karine's AI Skincare Advisor",
    title: "Tell Lumi what's going on with your skin.",
    lede: "Lumi reads Karine's live boutique — SkinCeuticals, Eminence Organics, Colorescience — and builds a personalized routine with an expert reason for every product she picks. Free, instant, and always grounded in what's actually on Karine's shelf.",
    placeholder: "e.g. “What is #12?” · “Book A7 in Ottawa Friday” · or describe your skin: I'm 38, combination skin, dullness…",
    chipsLabel: "Or start with a common concern —",
    chips: [
      "Fine lines & aging",
      "Hormonal breakouts",
      "Pigmentation & dark spots",
      "Dryness & dehydration",
      "Sensitive / rosacea",
      "Post-procedure recovery",
      "SPF & prevention",
      "Sparse lashes",
    ],
    submit: "Ask Lumi",
    submitLoading: "Lumi is thinking…",
    reset: "Start a new consult",
    resultHeading: "Lumi's read on your skin",
    reasonLabel: "Why this works for you",
    reserveRx: "Reserve at clinic · Rx",
    viewProduct: "View product",
    price: (n) => `$${Number(n).toFixed(2)} CAD`,
    errorGeneric: "Lumi is briefly offline — please try again in a moment.",
    minChars: "Give Lumi a little more to work with (3+ characters).",
    disclaimer: "Lumi is AI-guided from Karine's live boutique. Karine reviews new formulas with you at your next visit.",
    // "Need to book?" quick-action inside the initial input state — Feb 2026
    // ask ("add a small quick-action inside Lumi's modal now that the Ask
    // Karine pill is retired").
    bookHelpEyebrow: "Not shopping? Need a visit?",
    bookHelpBody: "Lumi handles routines. For appointments, injectables, facials or laser, our booking flow gets you on the schedule in seconds.",
    bookHelpCta: "Book a visit →",
    // Save-my-routine block
    saveTitle: "Save this routine",
    saveLede: "Get it in your inbox so you can shop from bed at 10pm. One click.",
    nameLabel: "First name (optional)",
    emailLabel: "Your email",
    emailPlaceholder: "you@example.com",
    subscribeInsider: "Send me Karine's weekly Insider Tips (skincare secrets, offers, launches)",
    saveCta: "Email me my routine ✨",
    saveSending: "Sending…",
    saveSent: "Sent! Check your inbox.",
    saveErrorEmail: "Please enter a valid email.",
    saveErrorGeneric: "Sorry — we couldn't send the email. Try again in a moment.",
    listen: "Hear Lumi",
    listenStop: "Stop",
    listenLoading: "Loading voice…",
  },
  fr: {
    eyebrow: "Rencontrez Lumi · Votre conseillère beauté IA",
    title: "Dites-moi ce qui se passe avec votre peau.",
    lede: "Je suis Lumi — je consulte la boutique en direct de Karine (SkinCeuticals, Eminence Organics, Colorescience) et je vous bâtis une routine sur mesure, avec l'explication de pourquoi chaque produit vous convient. C'est gratuit, instantané, et toujours basé sur ce qui est réellement en boutique.",
    placeholder: "ex. « C'est quoi le #12 ? » · « Réserver A7 à Ottawa vendredi » · ou décrivez votre peau…",
    chipsLabel: "Ou commençons par une préoccupation courante —",
    chips: [
      "Ridules & vieillissement",
      "Boutons hormonaux",
      "Pigmentation & taches",
      "Sécheresse & déshydratation",
      "Peau sensible / rosacée",
      "Récupération post-soin",
      "FPS & prévention",
      "Cils clairsemés",
    ],
    submit: "Demander à Lumi",
    submitLoading: "Lumi analyse votre peau…",
    reset: "Recommencer une consultation",
    resultHeading: "Mon analyse pour vous",
    reasonLabel: "Pourquoi ce produit vous convient",
    reserveRx: "Réserver en clinique · Rx",
    viewProduct: "Voir le produit",
    price: (n) => `${Number(n).toFixed(2)} $ CAD`,
    errorGeneric: "Je suis momentanément hors ligne — pouvez-vous réessayer dans un instant ?",
    minChars: "Donnez-moi un peu plus de détails pour bien vous conseiller (3 caractères min.).",
    disclaimer: "Lumi est guidée par IA à partir de la boutique en direct de Karine. Karine révise vos formules à votre prochain rendez-vous.",
    // "Besoin d'un rendez-vous ?" quick-action — Feb 2026
    bookHelpEyebrow: "Pas pour la boutique ? Besoin d'un rendez-vous ?",
    bookHelpBody: "Lumi construit des routines. Pour les injections, soins faciaux ou laser, notre outil de réservation vous place à l'horaire en quelques secondes.",
    bookHelpCta: "Prendre rendez-vous →",
    // Save-my-routine
    saveTitle: "Sauvegarder cette routine",
    saveLede: "Recevez-la dans votre boîte courriel pour magasiner à 22 h dans votre lit. Un clic.",
    nameLabel: "Prénom (optionnel)",
    emailLabel: "Votre courriel",
    emailPlaceholder: "vous@exemple.com",
    subscribeInsider: "Envoyez-moi les Insider Tips hebdo de Karine (secrets, offres, nouveautés)",
    saveCta: "Envoyez-moi ma routine ✨",
    saveSending: "Envoi en cours…",
    saveSent: "Envoyée ! Vérifiez votre boîte courriel.",
    saveErrorEmail: "Veuillez entrer un courriel valide.",
    saveErrorGeneric: "Désolée — impossible d'envoyer le courriel. Réessayez dans un instant.",
    listen: "Écouter Lumi",
    listenStop: "Arrêter",
    listenLoading: "Chargement voix…",
  },
};

// Small animated mascot — floating, breathing glow, twinkling sparkles.
function LumiMascot({ thinking = false, size = 88 }) {
  return (
    <div
      className={`relative flex-shrink-0 ${thinking ? "animate-bounce-soft" : "animate-float-soft"}`}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      {/* Glow ring */}
      <div
        className={`absolute inset-0 rounded-full bg-gradient-to-tr from-fuchsia-400 via-amber-300 to-emerald-400 opacity-70 blur-xl ${thinking ? "animate-pulse" : ""}`}
      />
      <svg
        viewBox="0 0 100 100"
        className="relative w-full h-full drop-shadow-lg"
      >
        <defs>
          <radialGradient id="lumi-body" cx="50%" cy="35%" r="70%">
            <stop offset="0%"  stopColor="#fef3c7" />
            <stop offset="55%" stopColor="#fbcfe8" />
            <stop offset="100%" stopColor="#a78bfa" />
          </radialGradient>
          <radialGradient id="lumi-cheek" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#fb7185" stopOpacity="0.75" />
            <stop offset="100%" stopColor="#fb7185" stopOpacity="0" />
          </radialGradient>
        </defs>
        {/* Head */}
        <circle cx="50" cy="52" r="36" fill="url(#lumi-body)" stroke="#ffffff" strokeWidth="1.5" />
        {/* Cheeks */}
        <circle cx="34" cy="60" r="7" fill="url(#lumi-cheek)" />
        <circle cx="66" cy="60" r="7" fill="url(#lumi-cheek)" />
        {/* Eyes — blink animation */}
        <g className="lumi-eyes">
          <ellipse cx="40" cy="50" rx="3.2" ry="4" fill="#1f2937" />
          <ellipse cx="60" cy="50" rx="3.2" ry="4" fill="#1f2937" />
          {/* Eye shine */}
          <circle cx="41" cy="48.5" r="1" fill="#ffffff" />
          <circle cx="61" cy="48.5" r="1" fill="#ffffff" />
        </g>
        {/* Smile */}
        <path d="M 40 66 Q 50 72 60 66" stroke="#1f2937" strokeWidth="2" fill="none" strokeLinecap="round" />
        {/* Antenna sparkle */}
        <line x1="50" y1="16" x2="50" y2="10" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" />
        <circle cx="50" cy="8" r="3" fill="#fbbf24" className="animate-pulse" />
      </svg>
      {/* Floating twinkles */}
      <span className="absolute -top-2 -right-2 text-amber-400 animate-twinkle">✦</span>
      <span className="absolute -bottom-1 -left-1 text-fuchsia-400 animate-twinkle-delayed">✦</span>
    </div>
  );
}

export default function SkinAdvisorBox({ language = "en" }) {
  const t = COPY[language] || COPY.en;
  const tx = LUMI_EXTRA[language] || LUMI_EXTRA.en;
  const [query, setQuery] = useState("");
  const [photo, setPhoto] = useState(null);
  const [spokenLang, setSpokenLang] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [bookingProposal, setBookingProposal] = useState(null);
  const [bookingBase, setBookingBase] = useState("");

  const submit = async (overrideQuery, forcedLocation) => {
    const q = (overrideQuery ?? query).trim();
    const isCode = /^#?\s*[AL]?\d{1,3}\s*[?!.]*$/i.test(q);
    if (q.length < 3 && !photo && !isCode) {
      setError(t.minChars);
      return;
    }
    setError("");
    setLoading(true);
    // Oct 2026 · Lumi Booking Assist — "Makeover 9, Ottawa, next Friday"
    // becomes a one-tap booking proposal instead of a skincare routine.
    if (!photo) {
      try {
        const bq = forcedLocation ? `${q} (clinic: ${forcedLocation})` : q;
        const { data: bi } = await axios.post(`${API}/lumi/book-intent`, { query: bq, language }, { timeout: 90000 });
        if (bi?.is_booking) {
          setBookingProposal(bi);
          setBookingBase(q);
          setLoading(false);
          return;
        }
      } catch { /* fall through to the skincare advisor */ }
    }
    try {
      const { data } = await axios.post(`${API}/store/skin-advisor/recommend`, {
        query: q,
        language,
        include_rx: true,
        image_base64: photo || undefined,
        spoken_language: spokenLang || undefined,
      }, { timeout: 180000 });
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    stopVoice();
    setBookingProposal(null);
    setBookingBase("");
    setQuery("");
    setPhoto(null);
    setSpokenLang(null);
    setResult(null);
    setError("");
    setSaveState({ email: "", name: "", subscribe: true, status: "idle", msg: "" });
  };

  // Save-my-routine state — Iter 402.5 · Aug 2026 · Karine ask
  const [saveState, setSaveState] = useState({
    email: "",
    name: "",
    subscribe: true, // opt-in default so Karine grows her list
    status: "idle",  // idle | sending | sent | error
    msg: "",
  });

  // ── Lumi Voice (kiosk-friendly) · Feb 2026 · Karine ask ──────────────
  // Guests with wet hands can hear Lumi's recommendation without touching
  // the screen. Hits POST /api/tts/lumi-speak (ElevenLabs · streams MP3).
  // Auto-plays when we're rendered inside the kiosk (URL has `src=kiosk`
  // or `kiosk=1`); otherwise the play button is opt-in.
  const audioRef = useRef(null);
  const [voice, setVoice] = useState({ status: "idle", url: "" }); // idle | loading | playing | error
  const kioskMode = (() => {
    if (typeof window === "undefined") return false;
    const p = new URLSearchParams(window.location.search);
    return p.get("src") === "kiosk" || p.get("kiosk") === "1" || window.location.pathname.startsWith("/kiosk");
  })();

  const buildSpokenScript = (r) => {
    if (!r) return "";
    const parts = [];
    if (r.summary) parts.push(r.summary);
    (r.products || []).slice(0, 3).forEach((p, i) => {
      const step = p.routine_step ? `${p.routine_step}: ` : "";
      const brand = p.brand ? `${p.brand} ` : "";
      parts.push(`Pick ${i + 1}. ${step}${brand}${p.name}. ${p.why_this_product || ""}`);
    });
    if (r.routine_plan?.actives_schedule) {
      parts.push(r.routine_plan.actives_schedule);
    }
    if (r.spa_ritual?.name) {
      parts.push(`You may also love our ${r.spa_ritual.name} spa ritual.`);
    }
    return parts.join(" ").slice(0, 780);
  };

  const stopVoice = () => {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    } catch (_) {}
    if (voice.url) {
      try { URL.revokeObjectURL(voice.url); } catch (_) {}
    }
    setVoice({ status: "idle", url: "" });
  };

  const playVoice = async () => {
    if (voice.status === "playing" || voice.status === "loading") {
      stopVoice();
      return;
    }
    const text = buildSpokenScript(result);
    if (!text) return;
    setVoice({ status: "loading", url: "" });
    try {
      const res = await axios.post(
        `${API}/tts/lumi-speak`,
        { text },
        { responseType: "blob", timeout: 30000 }
      );
      const url = URL.createObjectURL(res.data);
      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.onended = () => stopVoice();
        try { await audioRef.current.play(); } catch (_) {}
      }
      setVoice({ status: "playing", url });
    } catch (_e) {
      setVoice({ status: "error", url: "" });
      setTimeout(() => setVoice({ status: "idle", url: "" }), 2500);
    }
  };

  // Auto-play once when result appears in kiosk mode.
  useEffect(() => {
    if (result && kioskMode && voice.status === "idle") {
      // small delay so the card can fade in first
      const t = setTimeout(() => { playVoice(); }, 400);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result?.session_id]);

  // Stop voice on unmount
  useEffect(() => () => stopVoice(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveRoutine = async () => {
    const email = saveState.email.trim().toLowerCase();
    if (!email || !email.includes("@") || !email.split("@")[1]?.includes(".")) {
      setSaveState((s) => ({ ...s, status: "error", msg: t.saveErrorEmail }));
      return;
    }
    setSaveState((s) => ({ ...s, status: "sending", msg: "" }));
    try {
      await axios.post(`${API}/store/skin-advisor/email-routine`, {
        session_id: result.session_id,
        email,
        first_name: saveState.name.trim() || null,
        subscribe_insider_tips: !!saveState.subscribe,
      });
      setSaveState((s) => ({ ...s, status: "sent", msg: t.saveSent }));
    } catch (e) {
      setSaveState((s) => ({
        ...s,
        status: "error",
        msg: e?.response?.data?.detail || t.saveErrorGeneric,
      }));
    }
  };

  return (
    <section
      id="lumi-advisor"
      data-testid="skin-advisor-box"
      className="max-w-7xl mx-auto px-4 sm:px-6 mt-6 mb-4"
    >
      <div className="relative overflow-hidden rounded-[28px] border-2 border-fuchsia-300/60 bg-gradient-to-br from-fuchsia-600 via-rose-500 to-amber-400 shadow-2xl">
        {/* Decorative animated glows */}
        <div className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full bg-fuchsia-300/40 blur-3xl animate-glow-drift" />
        <div className="pointer-events-none absolute -bottom-28 -left-24 w-96 h-96 rounded-full bg-amber-300/40 blur-3xl animate-glow-drift-slow" />
        <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full opacity-30" style={{ background: "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.35) 0%, transparent 50%)" }} />

        {/* Inner card */}
        <div className="relative m-1 rounded-[24px] bg-gradient-to-br from-white via-white to-amber-50/60 p-5 sm:p-8">
          {/* Header row: mascot + eyebrow */}
          <div className="flex items-start gap-4 mb-4">
            <LumiMascot thinking={loading} />
            <div className="min-w-0">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-fuchsia-600 to-rose-500 text-white text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-bold shadow">
                <Sparkles className="w-3 h-3" />
                {t.eyebrow}
              </div>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-slate-900 leading-tight mt-3 max-w-3xl">
                {t.title}
              </h2>
              <p className="text-sm sm:text-base text-slate-700 mt-2 max-w-3xl leading-relaxed">
                {t.lede}
              </p>
            </div>
          </div>

          {/* Input state */}
          {!result && !bookingProposal && (
            <div className="mt-5 space-y-3">
              <textarea
                data-testid="skin-advisor-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t.placeholder}
                rows={3}
                maxLength={1200}
                disabled={loading}
                className="w-full rounded-2xl border-2 border-fuchsia-200 bg-white px-4 py-3 text-sm sm:text-[15px] text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-fuchsia-200 focus:border-fuchsia-500 shadow-sm resize-none disabled:opacity-70 transition"
              />

              <div className="flex flex-wrap items-center gap-2" data-testid="lumi-input-tools">
                <PhotoButton photo={photo} onPhoto={setPhoto} onClear={() => setPhoto(null)} label={tx} disabled={loading} />
                <MicButton
                  label={tx}
                  disabled={loading}
                  onTranscript={(text, lang) => {
                    if (!text) return;
                    setSpokenLang(lang);
                    const next = query ? `${query.trim()} ${text}` : text;
                    setQuery(next);
                    // Voice booking: "book twelve in Ottawa" / "what is number A seven" → run straight away
                    if (/\b(book|réserv|reserv|number|numéro|numero|hashtag|what is|c'est quoi|appoint|rendez)\b|#|\b[AL]\s?\d{1,3}\b|\b(one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty|un|deux|trois|quatre|cinq|six|sept|huit|neuf|dix|onze|douze|treize|quatorze|quinze|seize|vingt|trente)\b/i.test(text)) {
                      setTimeout(() => submit(next), 50);
                    }
                  }}
                />
                <span className="text-[11px] text-slate-500 italic basis-full sm:basis-auto sm:ml-1">{tx.toolsHint}</span>
              </div>

              <div>
                <p className="text-xs uppercase tracking-widest text-fuchsia-700 font-bold mb-2">
                  {t.chipsLabel}
                </p>
                <div className="flex flex-wrap gap-2">
                  {t.chips.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => {
                        setQuery(c);
                        submit(c);
                      }}
                      disabled={loading}
                      data-testid={`skin-advisor-chip-${c.replace(/\W+/g, "-").toLowerCase()}`}
                      className="text-xs sm:text-sm px-3.5 py-2 rounded-full bg-white border border-slate-200 hover:border-fuchsia-400 hover:bg-fuchsia-50 hover:text-fuchsia-800 text-slate-700 transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              {error && (
                <div
                  className="flex items-start gap-2 text-sm text-rose-800 bg-rose-50 border border-rose-300 rounded-xl px-3 py-2"
                  data-testid="skin-advisor-error"
                >
                  <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex items-center justify-between gap-3 pt-1">
                <p className="text-[11px] text-slate-500 italic max-w-xs sm:max-w-md">
                  {t.disclaimer}
                </p>
                <button
                  type="button"
                  onClick={() => submit()}
                  disabled={loading || (query.trim().length < 3 && !photo && !/^#?\s*[AL]?\d{1,3}\s*[?!.]*$/i.test(query.trim()))}
                  data-testid="skin-advisor-submit"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-fuchsia-600 via-rose-500 to-amber-500 hover:from-fuchsia-700 hover:via-rose-600 hover:to-amber-600 text-white font-bold text-sm px-6 py-3 rounded-full shadow-lg disabled:opacity-60 disabled:cursor-not-allowed transition transform hover:-translate-y-0.5"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t.submitLoading}
                    </>
                  ) : (
                    <>
                      {t.submit}
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>

              {/* Book a visit quick-action — Karine ask Feb 2026. Discoverable
                  fallback path now that the standalone Ask Karine pill is
                  retired. Uses the same /book deep-link convention with
                  `src=lumi_advisor_book_chip` for attribution. */}
              <div
                className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 rounded-2xl border border-fuchsia-200 bg-gradient-to-br from-white via-fuchsia-50/70 to-amber-50/60 px-4 py-3"
                data-testid="skin-advisor-book-chip"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-fuchsia-700">
                    {t.bookHelpEyebrow}
                  </p>
                  <p className="text-xs sm:text-sm text-slate-700 mt-0.5 leading-snug">
                    {t.bookHelpBody}
                  </p>
                </div>
                <a
                  href="/book?src=lumi_advisor_book_chip"
                  data-testid="skin-advisor-book-chip-cta"
                  className="shrink-0 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold shadow transition-transform hover:-translate-y-0.5"
                >
                  {t.bookHelpCta}
                </a>
              </div>
            </div>
          )}

          {bookingProposal && (
            <LumiBookingProposal
              p={bookingProposal}
              isFr={language === "fr"}
              onReset={reset}
              onPickLocation={(id) => submit(bookingBase, { 1: "Carleton Place", 2: "Kanata", 3: "Ottawa" }[id])}
            />
          )}

          {/* Result state */}
          {result && (
            <div className="mt-6 space-y-5" data-testid="skin-advisor-result">
              {/* Hidden audio element for kiosk voice playback */}
              <audio ref={audioRef} preload="none" data-testid="skin-advisor-audio" />
              {result.summary && (
                <div className="rounded-2xl bg-white border-2 border-fuchsia-200 p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3 mb-1.5">
                    <p className="text-[10px] uppercase tracking-widest text-fuchsia-700 font-bold flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5" /> {t.resultHeading}
                    </p>
                    <button
                      type="button"
                      onClick={playVoice}
                      disabled={voice.status === "loading"}
                      data-testid="skin-advisor-listen"
                      aria-label={voice.status === "playing" ? t.listenStop : t.listen}
                      className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold shadow-sm transition-transform hover:-translate-y-0.5 disabled:opacity-60 ${
                        voice.status === "playing"
                          ? "bg-rose-600 hover:bg-rose-700 text-white"
                          : "bg-gradient-to-r from-fuchsia-600 to-rose-500 hover:from-fuchsia-700 hover:to-rose-600 text-white"
                      }`}
                    >
                      {voice.status === "loading" ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          {t.listenLoading}
                        </>
                      ) : voice.status === "playing" ? (
                        <>
                          <VolumeX className="w-3.5 h-3.5" />
                          {t.listenStop}
                        </>
                      ) : (
                        <>
                          <Volume2 className="w-3.5 h-3.5" />
                          {t.listen}
                        </>
                      )}
                    </button>
                  </div>
                  <p className="text-sm sm:text-[15px] text-slate-800 leading-relaxed">
                    {result.summary}
                  </p>
                  {result.routine_note && (
                    <p className="text-xs sm:text-sm text-slate-700 italic mt-2 border-l-4 border-amber-400 pl-3 bg-amber-50/50 py-1.5 rounded-r-md">
                      {result.routine_note}
                    </p>
                  )}
                </div>
              )}

              <PhotoFindings findings={result.photo_findings} t={tx} />

              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {result.products?.map((p) => (
                  <ProductCard key={p.sku} product={p} t={t} />
                ))}
              </div>

              <RoutinePlan plan={result.routine_plan} t={tx} />

              {result.caveat && (
                <div className="flex items-start gap-2 text-xs sm:text-sm text-amber-900 bg-amber-50 border-2 border-amber-300 rounded-xl px-3 py-2.5">
                  <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{result.caveat}</span>
                </div>
              )}

              {/* Spa ritual add-on — Iter 402.8 · Karine ask.
                  Fired when the client's own words include wellness /
                  relaxation / post-procedure cues. Uses the Aveda forest
                  palette for Aveda picks and the Éminence honey palette
                  for Éminence picks so the two brands stay clearly
                  distinct on-screen. */}
              {result.spa_ritual && (
                <SpaRitualCard ritual={result.spa_ritual} isFr={language === "fr"} />
              )}

              {/* Save-my-routine block — Iter 402.5 · Aug 2026 · Karine ask */}
              <div
                className="rounded-2xl border-2 border-fuchsia-200 bg-gradient-to-br from-fuchsia-50 via-rose-50 to-amber-50 p-4 sm:p-5 shadow-sm"
                data-testid="save-routine-block"
              >
                {saveState.status === "sent" ? (
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Check className="w-5 h-5 text-emerald-700" />
                    </div>
                    <div>
                      <p className="font-serif text-lg text-slate-900">{t.saveSent}</p>
                      <p className="text-sm text-slate-600 mt-1">
                        {saveState.email}
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-fuchsia-500 to-rose-500 flex items-center justify-center flex-shrink-0 shadow">
                        <Mail className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-serif text-lg sm:text-xl text-slate-900 leading-tight">
                          {t.saveTitle}
                        </p>
                        <p className="text-xs sm:text-sm text-slate-600 mt-0.5">
                          {t.saveLede}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                      <input
                        type="text"
                        value={saveState.name}
                        onChange={(e) => setSaveState((s) => ({ ...s, name: e.target.value, status: "idle" }))}
                        placeholder={t.nameLabel}
                        maxLength={80}
                        disabled={saveState.status === "sending"}
                        data-testid="save-routine-name"
                        className="w-full rounded-xl border border-fuchsia-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300 focus:border-fuchsia-500 disabled:opacity-70"
                      />
                      <input
                        type="email"
                        value={saveState.email}
                        onChange={(e) => setSaveState((s) => ({ ...s, email: e.target.value, status: "idle" }))}
                        placeholder={t.emailPlaceholder}
                        maxLength={180}
                        disabled={saveState.status === "sending"}
                        data-testid="save-routine-email"
                        className="w-full rounded-xl border border-fuchsia-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300 focus:border-fuchsia-500 disabled:opacity-70"
                      />
                    </div>

                    <label className="flex items-start gap-2 text-xs sm:text-[13px] text-slate-700 mb-3 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={saveState.subscribe}
                        onChange={(e) => setSaveState((s) => ({ ...s, subscribe: e.target.checked }))}
                        disabled={saveState.status === "sending"}
                        data-testid="save-routine-insider-optin"
                        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-fuchsia-600 focus:ring-fuchsia-400"
                      />
                      <span>{t.subscribeInsider}</span>
                    </label>

                    {saveState.status === "error" && (
                      <div
                        className="flex items-start gap-2 text-xs text-rose-800 bg-rose-50 border border-rose-300 rounded-lg px-3 py-2 mb-3"
                        data-testid="save-routine-error"
                      >
                        <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{saveState.msg}</span>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={saveRoutine}
                      disabled={saveState.status === "sending" || !saveState.email.trim()}
                      data-testid="save-routine-submit"
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-fuchsia-600 via-rose-500 to-amber-500 hover:from-fuchsia-700 hover:via-rose-600 hover:to-amber-600 text-white font-bold text-sm px-6 py-3 rounded-full shadow-lg disabled:opacity-60 disabled:cursor-not-allowed transition transform hover:-translate-y-0.5"
                    >
                      {saveState.status === "sending" ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          {t.saveSending}
                        </>
                      ) : (
                        <>
                          <Mail className="w-4 h-4" />
                          {t.saveCta}
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>

              <div className="flex items-center justify-between gap-3">
                <p className="text-[11px] text-slate-500 italic">{t.disclaimer}</p>
                <button
                  type="button"
                  onClick={reset}
                  data-testid="skin-advisor-reset"
                  className="inline-flex items-center gap-1.5 text-sm text-fuchsia-800 hover:text-fuchsia-900 font-bold"
                >
                  <RefreshCcw className="w-4 h-4" />
                  {t.reset}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function ProductCard({ product, t }) {
  const {
    sku, name, brand, price_cad, size_oz, image_url, is_prescription,
    routine_step, why_this_product, priority,
  } = product;
  const isStartHere = priority === 1;
  return (
    <article
      data-testid={`advisor-pick-${sku}`}
      className={`relative flex gap-3 rounded-2xl transition p-3 ${
        isStartHere
          ? "bg-gradient-to-br from-amber-50 via-white to-fuchsia-50 border-2 border-amber-500 shadow-lg ring-2 ring-amber-500/20"
          : "bg-white border border-slate-200 hover:border-fuchsia-400 hover:shadow-lg"
      }`}
    >
      {/* Priority ranking pill — top-left. Karine ask (Feb 28 2026):
          rank #1 gets "Start Here", the rest get "№ 2 · Next" etc. */}
      {priority != null && (
        <div
          className={`absolute -top-2.5 -left-2.5 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-widest shadow-md ${
            isStartHere
              ? "bg-gradient-to-r from-amber-500 to-fuchsia-600 text-white"
              : "bg-white text-slate-700 border border-slate-300"
          }`}
          data-testid={`advisor-pick-${sku}-priority`}
        >
          {isStartHere ? <>★ Start Here</> : <>№ {priority}</>}
        </div>
      )}
      <Link
        to={`/shop/${sku}`}
        className="flex-shrink-0 w-24 h-24 sm:w-28 sm:h-28 rounded-xl bg-gradient-to-br from-amber-50 to-fuchsia-50 overflow-hidden"
      >
        <img
          src={image_url || "/product-fallback.png"}
          alt={name}
          className="w-full h-full object-contain p-2"
          loading="lazy"
        />
      </Link>
      <div className="flex-1 min-w-0 flex flex-col">
        {routine_step && (
          <p className="text-[10px] uppercase tracking-widest text-fuchsia-700 font-bold truncate">
            {routine_step}
          </p>
        )}
        <Link to={`/shop/${sku}`} className="block">
          <p className="text-[12px] text-slate-500 truncate">{brand}</p>
          <h3 className="text-sm sm:text-[15px] font-semibold text-slate-900 leading-tight line-clamp-2 hover:text-fuchsia-800">
            {name}
          </h3>
        </Link>
        <div className="flex items-center gap-2 mt-1 text-xs">
          {price_cad != null && (
            <span className="font-bold text-slate-900">{t.price(price_cad)}</span>
          )}
          {size_oz && <span className="text-slate-500">· {size_oz}</span>}
        </div>
        <p className="text-[12.5px] text-slate-700 leading-snug mt-1.5">
          <span className="text-[10px] uppercase tracking-widest text-fuchsia-700 font-bold mr-1">
            {t.reasonLabel}:
          </span>
          {why_this_product}
        </p>
        <div className="mt-auto pt-2">
          <Link
            to={`/shop/${sku}`}
            className={`inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full transition ${
              is_prescription
                ? "bg-rose-600 text-white hover:bg-rose-700"
                : "bg-fuchsia-700 text-white hover:bg-fuchsia-800"
            }`}
            data-testid={`advisor-cta-${sku}`}
          >
            {is_prescription ? t.reserveRx : t.viewProduct}
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </article>
  );
}



// SpaRitualCard — surfaces an Aveda or Éminence facial as an add-on
// when Lumi detects wellness / relaxation / post-procedure cues.
function SpaRitualCard({ ritual, isFr }) {
  const isAveda = ritual.brand === "Aveda";
  // Forest palette for Aveda, honey/earth palette for Éminence.
  const P = isAveda
    ? {
        bgTop: "#0a1f14", bgMid: "#0f3025", bgBot: "#2d6b52",
        accent: "#e6c88a", accentText: "#0a1f14", eyebrow: "Aveda · Signature Ritual",
      }
    : {
        bgTop: "#2b1a0a", bgMid: "#3d2916", bgBot: "#82956f",
        accent: "#e5c07a", accentText: "#2b1a0a", eyebrow: "Éminence · Organic Ritual",
      };

  // Iter 402.9 · Aug 2026 · Karine ask —
  // "When a client taps Book directly from Lumi's spa card, pre-select
  //  the exact facial + provider in the booking flow so they land one
  //  tap from confirming."
  // Piggy-backs on the existing `/book?service=&note=&provider=` deep-link
  // that HomePage.jsx already reads (see the promo-card prefill flow).
  // `service` auto-matches the treatment in Square; `note` gives Karine
  // context on the Square checkout; `provider` tells the booking flow
  // that a medical aesthetician (not an injector) should be shown first.
  const noteText = isFr
    ? `Ritual pré-sélectionné par Lumi (Conseillère IA skincare) : ${ritual.name}. Prix ${ritual.price_cad} $ CAD · ${ritual.minutes} min.`
    : `Pre-selected by Lumi (AI Skincare Advisor): ${ritual.name}. Price $${ritual.price_cad} CAD · ${ritual.minutes} min.`;
  const bookHref =
    `/book?service=${encodeURIComponent(ritual.name)}` +
    `&provider=aesthetician` +
    `&note=${encodeURIComponent(noteText)}` +
    `&src=lumi_advisor`;

  return (
    <div
      data-testid={`advisor-spa-ritual-${ritual.key}`}
      className="rounded-2xl overflow-hidden shadow-lg"
      style={{ background: `linear-gradient(135deg, ${P.bgTop} 0%, ${P.bgMid} 55%, ${P.bgBot} 100%)`, color: "#f5f0e6" }}
    >
      <div className="p-5 sm:p-6">
        <div
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full mb-3"
          style={{ background: `${P.accent}22`, border: `1px solid ${P.accent}55`, color: P.accent }}
        >
          <span className="text-[10px] uppercase tracking-[0.28em] font-bold">{P.eyebrow}</span>
        </div>
        <p className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: P.accent }}>
          {isFr ? "Ajoutez à votre routine · Rituel spa" : "Add to your routine · Spa ritual"}
        </p>
        <h3
          className="font-serif text-xl sm:text-2xl leading-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif", color: "#f5f0e6" }}
        >
          {ritual.name}
        </h3>
        <div className="flex items-center gap-3 mt-2 text-sm" style={{ color: "rgba(245,240,230,0.85)" }}>
          <span className="font-semibold">${ritual.price_cad} CAD</span>
          <span>· {ritual.minutes} min</span>
        </div>
        {ritual.why && (
          <p className="text-sm leading-relaxed mt-3" style={{ color: "rgba(245,240,230,0.95)" }}>
            {ritual.why}
          </p>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          <a
            href={ritual.url}
            data-testid={`advisor-spa-cta-${ritual.key}`}
            className="inline-flex items-center gap-1 px-4 py-2 rounded-full font-bold text-xs transition-transform hover:-translate-y-0.5"
            style={{ background: P.accent, color: P.accentText }}
          >
            {isFr ? "Voir ce rituel" : "See this ritual"}
            <ArrowRight className="w-3 h-3" />
          </a>
          <a
            href={bookHref}
            data-testid={`advisor-spa-book-${ritual.key}`}
            className="inline-flex items-center gap-1 px-4 py-2 rounded-full font-semibold text-xs transition"
            style={{ background: "transparent", color: "#f5f0e6", border: `1px solid ${P.accent}88` }}
          >
            {isFr ? "Réserver directement" : "Book directly"}
          </a>
        </div>
      </div>
    </div>
  );
}
